<template>
  <div
    v-tooltip="{
      content: t('disabled-in-preview-mode'),
      shown: showPreviewTooltip,
      distance: 10,
      triggers: ['click'],
    }"
  >
    <div
      v-tooltip="
        showToolTipOnHover
          ? {
              content: requiredMessage,
              shown: showTooltip,
              distance: 10,
              placement: Placement.Top,
            }
          : null
      "
      :class="['document-attachment', { focused, disabled: isPreview }]"
      @click="handleClick(documentAttachment)"
      @mouseover="setHover(true)"
      @mouseleave="setHover(false)"
    >
      <FriedIcon :icon="icon" class="attachment-type-icon" />
      <div class="text-button-container">
        <div class="text-container">
          <FriedH4
            :class="['title', { required: !!requiredMessage }]"
            :data-external="`attachement-info-title-text-${title}`"
          >
            {{ title }}
          </FriedH4>
          <FriedParagraph
            v-if="showDescription"
            :size="ParagraphSize.Small"
            class="description"
            :data-external="`attachement-info-description-text-${description}`"
          >
            {{ description }}
          </FriedParagraph>
          <FriedParagraph
            v-if="showInteraction"
            :size="ParagraphSize.Small"
            class="interaction"
            :data-external="`attachement-info-${attachmentInteraction}-text`"
          >
            {{ attachmentInteraction }}
          </FriedParagraph>
        </div>
        <ThemedButton
          class="attachment-button"
          tab-index="0"
          :data-external="`required-step-${buttonText}-btn`"
          :centered="true"
          :theme="theme"
          :disable-hover="isPreview"
          :disabled="isPreview"
          :custom-hover="true"
          :is-hovered="hovered"
          @focus="setFocus(true)"
          @blur="setFocus(false)"
        >
          {{ buttonText }}
        </ThemedButton>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { AttachmentType } from '@getaccept/lib-shared-new/src/attachments/types/attachment-type';
import type { DocumentAttachment } from '@getaccept/lib-shared-new/src/types/document-attachment';
import ThemedButton from '@getaccept/lib-shared-new/src/components/ThemedButton.vue';
import type { SigningTheme } from '@getaccept/lib-shared-new/src/signing-theme/types/theme';
import type { PropType } from 'vue';
import { computed, defineComponent, ref, watch } from 'vue';
import type { IconName } from '@getaccept/fried-tofu';
import { ParagraphSize, Placement } from '@getaccept/fried-tofu';
import { IconHelper } from '@getaccept/lib-shared-new/src/helpers/icon.helper';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { DURATION } from '../../common/constants/tooltip';

export default defineComponent({
  components: {
    ThemedButton,
  },
  props: {
    documentAttachment: {
      type: Object as PropType<DocumentAttachment>,
      required: true,
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: () => ({}),
    },
    isSigned: {
      type: Boolean,
    },
    isPreview: {
      type: Boolean,
    },
  },
  emits: ['attachmentView', 'uploadFile'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const focused = ref(false);
    const showPreviewTooltip = ref(false);
    const hovered = ref(false);

    const watchFocusedRequiredId = (focusedRequiredId: string) => {
      if (props.documentAttachment.id === focusedRequiredId) {
        setFocus(true);
      } else {
        setFocus(false);
      }
    };

    watch(() => props.focusedRequiredId, watchFocusedRequiredId);

    const showDescription = computed(() => {
      if (props.documentAttachment.attachment.type !== AttachmentType.Upload) {
        return props.documentAttachment.requireView && !props.documentAttachment.lastView;
      }
      return !!props.documentAttachment.attachment.description;
    });

    const showInteraction = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload) {
        return !!props.documentAttachment.lastUpload;
      }
      return !!props.documentAttachment.lastView;
    });

    const description = computed(() => props.documentAttachment.attachment.description);

    const requiredMessage = computed(() => {
      if (props.isPreview || !props.documentAttachment.requireView) {
        return null;
      }
      return props.documentAttachment.attachment.type === AttachmentType.Upload
        ? t('required-upload')
        : t('requires-view');
    });

    const showTooltip = computed(() => {
      if (!requiredMessage.value) {
        return false;
      }
      return focused.value;
    });

    const showToolTipOnHover = computed(
      () =>
        !props.isPreview &&
        !props.documentAttachment.lastView &&
        !props.documentAttachment.lastUpload
    );

    const attachmentInteraction = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload) {
        const lastUpload = DateTime.fromISO(props.documentAttachment.lastUpload).toLocaleString(
          DateTime.DATE_SHORT
        );
        return `${t('uploaded-file')}: ${
          props.documentAttachment.attachment.filename
        }, ${lastUpload}`;
      }
      const lastView = DateTime.fromISO(props.documentAttachment.lastView);
      const time = lastView.toLocaleString(DateTime.TIME_SIMPLE);
      const date = lastView.toLocaleString(DateTime.DATE_SHORT);
      return `${t('viewed-at')} ${time} ${date}`;
    });

    const icon = computed<IconName>(() => {
      switch (props.documentAttachment.attachment.type) {
        case AttachmentType.Upload:
          return 'upload';
        case AttachmentType.External:
          return 'link';
        default:
          return IconHelper.getFileIcon(props.documentAttachment.attachment.filename);
      }
    });

    const title = computed(() => {
      if (props.documentAttachment.attachment.title) {
        return props.documentAttachment.attachment.title;
      }
      return t('attachment-title-not-found');
    });

    const buttonText = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload && !props.isSigned) {
        return !props.documentAttachment.lastUpload ? t('upload') : t('replace');
      }
      return t('show');
    });

    const handleClick = (documentAttachment: DocumentAttachment) => {
      const { id: documentAttachmentId, previewUrl, attachment } = documentAttachment;
      const { id, type, title } = attachment;
      switch (type) {
        case AttachmentType.Upload:
          if (props.isSigned) {
            openAttachmentInNewTab(documentAttachmentId, previewUrl);
            return;
          }
          if (props.isPreview) {
            togglePreviewTooltip();
            return;
          }
          emit('uploadFile', {
            attachmentId: id,
            name: title,
          });
          break;
        case AttachmentType.External:
        case AttachmentType.File:
        case AttachmentType.Document:
          if (props.isPreview) {
            togglePreviewTooltip();
            return;
          }

          if (previewUrl) {
            openAttachmentInNewTab(documentAttachmentId, previewUrl);
          }
      }
    };

    const openAttachmentInNewTab = (id: string, url: string) => {
      window.open(url);
      emit('attachmentView', id);
    };

    const setFocus = (hasFocus: boolean) => {
      focused.value = hasFocus;
    };

    const setHover = (hasHover: boolean) => {
      hovered.value = hasHover;
    };

    const togglePreviewTooltip = () => {
      if (showPreviewTooltip.value) {
        return;
      }
      showPreviewTooltip.value = true;
      setTimeout(() => {
        showPreviewTooltip.value = false;
      }, DURATION);
    };

    return {
      focused,
      showDescription,
      showInteraction,
      description,
      requiredMessage,
      showTooltip,
      showToolTipOnHover,
      attachmentInteraction,
      icon,
      title,
      buttonText,
      handleClick,
      setFocus,
      setHover,
      hovered,
      showPreviewTooltip,
      ParagraphSize,
      t,
      togglePreviewTooltip,
      openAttachmentInNewTab,
      watchFocusedRequiredId,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
.document-attachment {
  display: flex;
  align-items: center;
  background-color: var(--white);
  height: auto;
  padding: var(--spacing-50) var(--spacing-100);
  margin-top: var(--spacing-50);
  box-shadow: var(--shadow-1);
  transition: all 0.1s ease-in;
  outline: none;
  border-radius: var(--radius-medium);
  cursor: pointer;

  @media screen and (min-width: $md) {
    margin-top: var(--spacing-100);
  }

  &.disabled {
    cursor: auto;
    background-color: var(--white);
    box-shadow: var(--shadow-1);
  }

  .text-button-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .text-container {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding: 0 var(--spacing-100);

      .title {
        overflow: hidden;
        text-overflow: ellipsis;

        &.required::after {
          content: '*';
          color: var(--error-dark);
        }
      }

      .description,
      .interaction {
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--text-gray);
      }

      .interaction {
        color: var(--success-dark);
      }
    }

    .loader {
      margin-right: 3rem;
    }

    .attachment-button {
      min-width: 7rem;
      height: 100%;
      outline: none;
    }
  }
}
</style>
